/* The /onenote-viewer page.
 *
 * What someone wants from a .one file they cannot open is the words back. So
 * the notes are the page, and everything the file says about itself sits above
 * them: which format wrote it, how much was reached, who wrote it and when.
 *
 * The refusal matters as much as the reading. The second container in the same
 * spec — the package store, used for cloud-synced sections — is not read here,
 * and three of twelve real files carry it, including both called
 * "FromOffice365". Calling it "the old format" would send exactly the wrong
 * person away, so it is named for what it is.
 */

import { read } from './one-read.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__ONE_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first: ':n' is a prefix of ':name'. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { doc: null, name: '', limit: null }

function setStatus(msg, kind = 'info') {
  const box = $('#one-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'one-status lg-card one-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

async function open(file) {
  state.name = file.name
  setStatus(t('opening', 'Reading the section…'))
  $('#one-workspace').hidden = true
  $('#one-summary').hidden = true

  const bytes = new Uint8Array(await file.arrayBuffer())
  const t0 = performance.now()
  const doc = read(bytes)
  const ms = Math.round(performance.now() - t0)
  state.doc = doc

  /* Which OneNote formats people actually bring, and how much of each is
     reached. The count of files in the format this reader declines is the
     number that decides whether writing it is worth it — and there is no other
     way to learn it, because someone whose file will not open just leaves. */
  try {
    reportShape({
      format: 'one',
      version: doc.format,
      size: file.size,
      ok: doc.ok,
      reason: doc.problems[0] || null,
      flags: doc.problems,
      counts: {
        objects: doc.objects,
        fragments: doc.fragments,
        runs: doc.texts.length,
        authors: doc.authors.length,
        chars: doc.texts.reduce((n, r) => n + r.text.length, 0),
      },
    })
  } catch (_) { /* telemetry must never break the tool */ }

  /* The package store is READ now, so the old blanket refusal would turn away
     files this tool opens. It survives only for a package section nothing came
     out of, and for a container that is neither. */
  if (doc.format === 'unknown' || (doc.format === 'package' && !doc.texts.length)) {
    setStatus(t('err_legacy', 'This is a OneNote section in the sync packaging (package store) — the form OneNote saves cloud-synced files in. This tool reads only the plain revision store so far. Identifier: :guid.', { guid: doc.formatGuid || '—' }), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'onenote-viewer', document.querySelector('#one-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }
  if (!doc.format) {
    setStatus(t('err_not_one', 'This file is not a OneNote section.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'onenote-viewer', document.querySelector('#one-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }
  if (!doc.texts.length) {
    setStatus(t('warn_no_text', 'The section opened, but no note text was found in it — it may hold only images, attachments or an empty page.'), 'warn')
  } else {
    setStatus('')
  }

  renderSummary(ms, file.size)
  renderNotes()
  try { window.__stage?.('complete') } catch (_) {}
  $('#one-workspace').hidden = false
  state.limit?.commit()
  window.__aeToolComplete?.({ runs: doc.texts.length, objects: doc.objects })
}

function renderSummary(ms, fileSize) {
  const box = $('#one-summary')
  const d = state.doc
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'one-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'one-chip' + (kind ? ' one-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_size', 'Size'), fmtBytes(fileSize))
  chip(t('chip_runs', 'Text blocks'), d.texts.length)
  chip(t('chip_objects', 'Objects'), d.objects.toLocaleString())
  if (d.authors.length) chip(t('chip_authors', 'Authors'), d.authors.length)
  box.append(chips)

  const notes = el('ul', 'one-notes')
  const note = (s, key = false) => notes.append(el('li', key ? 'one-note--key' : null, s))
  if (d.authors.length) note(t('note_authors', 'Written by: :list', { list: d.authors.join(', ') }))
  if (d.stamps.length) note(t('note_stamps', 'Dates and times in the section: :list', { list: d.stamps.slice(0, 8).join(' · ') }))
  if (d.problems.includes('truncated')) {
    note(t('note_truncated', 'The section states a length larger than the file: it was cut short. Everything reachable before the cut is shown below.'), true)
  }
  note(t('note_read', 'Read in :ms ms — :objects objects, :runs blocks of text.', {
    ms, objects: d.objects.toLocaleString(), runs: d.texts.length,
  }))
  box.append(el('h4', 'one-sub', t('notes_title', 'What the file says about itself')), notes)
}

function renderNotes() {
  const host = $('#one-text')
  host.innerHTML = ''
  const d = state.doc
  if (!d.texts.length) {
    host.append(el('p', 'one-muted', t('no_text', 'No note text was found in this section.')))
    return
  }
  for (const run of d.texts) {
    const p = el('p', 'one-run')
    p.textContent = run.text
    host.append(p)
  }
}

const stem = () => state.name.replace(/\.one$/i, '') || 'notes'

function exportText() {
  const d = state.doc
  if (!d || !d.texts.length) return
  const head = []
  if (d.authors.length) head.push(t('note_authors', 'Written by: :list', { list: d.authors.join(', ') }))
  if (d.stamps.length) head.push(d.stamps.join(' · '))
  const body = d.texts.map(r => r.text).join('\n\n')
  const text = (head.length ? head.join('\n') + '\n\n' : '') + body + '\n'
  window.__downloadFile?.(new Blob([text], { type: 'text/plain;charset=utf-8' }), stem() + '.txt')
}

function wire() {
  const drop = document.getElementById('oneDrop')
  drop?.addEventListener('tool-uploader:file', async ev => {
    const file = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file
    if (!file) return
    /* Funnel stages. Without them a tool can only report that it finished —
       never that someone arrived, picked a file and got nothing, which is the
       case worth knowing about because that person leaves without writing in. */
    try { window.__stage?.('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
    state.limit = window.__toolLimit?.begin('onenote-viewer', file.name + ':' + file.size) || null
    try {
      await open(file)
    } catch (err) {
      try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'onenote-viewer', document.querySelector('#one-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
      setStatus(t('err_open', 'This file could not be read: ') + (err?.message || err), 'error')
      window.__handleError?.(err, 'onenote-viewer')
    }
  })
  drop?.addEventListener('tool-uploader:clear', () => {
    state.doc = null
    $('#one-workspace').hidden = true
    $('#one-summary').hidden = true
    setStatus('')
  })
  $('#one-export-txt')?.addEventListener('click', exportText)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
